import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, finalize } from 'rxjs';
import { Data2Service } from 'src/app/common/data2.service';
import { DriverLedgerApiService } from '@/drivers/driver-ledger/_api/driver-ledger.api.service';
import { DriverLedgerPendingSummary } from '@/drivers/driver-ledger/_models/driver-ledger.model';
import {
	DriverEnovaMapping,
	DriverIntegration,
	GratyfikantMapping,
} from '@/drivers/drivers-integration/_models/drivers-integration.model';
import { SeasonalBonusApiService } from '@/drivers/drivers-seasonal-bonuses/_api/seasonal-bonus.api.service';
import { DriverSeasonalBonus } from '@/drivers/drivers-seasonal-bonuses/_models/seasonal-bonus.model';
import { seasonalRateForDay } from '@/drivers/drivers-seasonal-bonuses/_utils/seasonal-bonus.utils';
import { SettlementBonusListV2ApiService } from '@/settlement/settlement-bonus-list-v2/_api/settlement-bonus-list-v2.api.service';
import { BulkActionResult } from '@/settlement/settlement-bonus-list-v2/_models/settlement-bonus-list-v2.model';
import { DriversSalaryV2ApiService } from '../_api/drivers-salary-v2.api.service';
import {
	DOJAZD_FLAT_AMOUNT,
	DojazdEventRow,
	DriverSettlementBonus,
	DriverTourismEvent,
	DriverWorkRate,
	GratyfikantSkippedOrganization,
	NavbridgeBonusesStats,
	NawigatorPullStats,
	PERIOD_TYPE_MONTHLY,
	PRACA_FALLBACK_RATE,
	PracaEventRow,
	SalaryCacheResponse,
	SalaryCashPayoutPayload,
	SalaryKasaSettlementPayload,
	SalaryDraft,
	SalaryDraftBonusPayload,
	SalaryDraftBonusRow,
	SalaryDraftEventPayload,
	SalaryDraftEventRow,
	SalaryDraftNettoPayload,
	SalaryDraftNettoRow,
	TECHNICZNY_FLAT_AMOUNT,
	TechnicznyEventRow,
} from '../_models/drivers-salary-v2.model';
import {
	bonusValueToGrosze,
	countDaysInMonth,
	eventMonthSpan,
	extractErrorMessage,
	getMonthRange,
	hasPeriodInMonth,
} from '../_utils/drivers-salary-v2.utils';

export interface SettlementBonusRow extends DriverSettlementBonus {
	amountGrosze: number;
	plnGrosze: number;
}

const EMPTY_LEDGER_PENDING_SUMMARY: DriverLedgerPendingSummary = {
	count: 0,
	totalPln: 0,
	totalEur: 0,
	totalEurPln: 0,
};

interface KasaSplit {
	settledPln: number;
	settledEur: number;
	settledEurPln: number;
	carriedPln: number;
	carriedEur: number;
}

/**
 * null means the whole balance, which is what every month did before a part of it could be carried.
 * An amount pointing the other way settles nothing - the backend refuses it, this just never shows a
 * deduction the payout will not make.
 */
function clampToBalance(chosen: number | null, balance: number): number {
	if (chosen === null) return balance;
	if (balance === 0 || chosen === 0) return 0;
	if (chosen > 0 !== balance > 0) return 0;

	return Math.abs(chosen) >= Math.abs(balance) ? balance : chosen;
}

/** The settled euro keeps the single NBP rate the whole balance was valued at, never a fresh one. */
function shareOfEurInPln(settledEur: number, balanceEur: number, balanceEurPln: number): number {
	if (balanceEur === 0 || settledEur === 0) return 0;
	if (settledEur === balanceEur) return balanceEurPln;

	const value = (settledEur * balanceEurPln) / balanceEur;

	return Math.sign(value) * Math.round(Math.abs(value));
}

@Injectable()
export class DriversSalaryV2StateService {
	readonly gratyfikantTotal = signal<number | null>(null);
	readonly gratyfikantLoading = signal(false);
	readonly gratyfikantLoaded = signal(false);
	readonly gratyfikantLoadedAt = signal<Date | null>(null);
	readonly gratyfikantError = signal<string | null>(null);
	readonly gratyfikantSkipped = signal<GratyfikantSkippedOrganization[]>([]);

	readonly enovaTotal = signal<number | null>(null);
	readonly enovaLoading = signal(false);
	readonly enovaLoaded = signal(false);
	readonly enovaLoadedAt = signal<Date | null>(null);
	readonly enovaError = signal<string | null>(null);

	readonly nawigatorLoading = signal(false);
	readonly nawigatorLoadedAt = signal<Date | null>(null);
	readonly nawigatorError = signal<string | null>(null);
	readonly nawigatorPullStats = signal<NawigatorPullStats | null>(null);

	readonly navbridgeBonusesLoading = signal(false);
	readonly navbridgeBonusesLoadedAt = signal<Date | null>(null);
	readonly navbridgeBonusesError = signal<string | null>(null);
	readonly navbridgeBonusesStats = signal<NavbridgeBonusesStats | null>(null);

	readonly tourismEvents = signal<DriverTourismEvent[]>([]);
	readonly tourismEventsLoading = signal(false);
	readonly eventExclusionError = signal<string | null>(null);

	readonly integration = signal<DriverIntegration | null>(null);
	readonly gratyfikantMappings = signal<GratyfikantMapping[]>([]);
	readonly enovaMappings = signal<DriverEnovaMapping[]>([]);
	readonly integrationLoading = signal(false);

	readonly workRates = signal<DriverWorkRate[]>([]);

	/** Seasons running for this driver in this month, clipped to it by the backend. */
	readonly seasonalBonuses = signal<DriverSeasonalBonus[]>([]);

	readonly ledgerPendingSummary = signal<DriverLedgerPendingSummary>(EMPTY_LEDGER_PENDING_SUMMARY);

	readonly settlementBonuses = signal<DriverSettlementBonus[]>([]);
	readonly settlementBonusesLoading = signal(false);
	readonly settlementActionLoading = signal(false);
	readonly settlementActionError = signal<string | null>(null);
	readonly recalculatePlnLoading = signal(false);

	readonly settlementNotes = signal<string>('');
	readonly paymentNotes = signal<string>('');
	readonly settledAt = signal<string | null>(null);
	readonly settledBy = signal<string | null>(null);
	readonly salaryId = signal<number | null>(null);
	readonly notesSaving = signal(false);
	readonly notesError = signal<string | null>(null);
	readonly settleLoading = signal(false);
	readonly settleError = signal<string | null>(null);

	readonly cashPayoutPln = signal<number | null>(null);
	readonly cashPayoutEur = signal<number | null>(null);
	readonly cashPayoutDate = signal<string | null>(null);
	/** Read-only here: it is stamped by whoever last changed the amount, never typed in. */
	readonly cashPayoutBy = signal<string | null>(null);
	readonly cashPayoutSaving = signal(false);
	readonly cashPayoutError = signal<string | null>(null);

	readonly kasaSettlePlnChoice = signal<number | null>(null);
	readonly kasaSettleEurChoice = signal<number | null>(null);
	readonly kasaSettleEurPlnFrozen = signal<number | null>(null);
	readonly kasaCarriedPlnFrozen = signal<number | null>(null);
	readonly kasaCarriedEurFrozen = signal<number | null>(null);
	readonly kasaEurInCash = signal(false);
	readonly kasaSettlementSaving = signal(false);
	readonly kasaSettlementError = signal<string | null>(null);

	readonly salaryDrafts = signal<SalaryDraft[]>([]);
	readonly draftsLoading = signal(false);
	readonly draftsError = signal<string | null>(null);
	readonly draftRejectingId = signal<number | null>(null);
	readonly draftAcceptingId = signal<number | null>(null);

	readonly draftActionBusy = computed(() => this.draftRejectingId() !== null || this.draftAcceptingId() !== null);

	readonly isSettled = computed(() => this.settledAt() !== null);

	readonly pendingDrafts = computed(() => this.salaryDrafts().filter((draft) => draft.status === 'pending'));

	readonly hasPendingDrafts = computed(() => this.pendingDrafts().length > 0);

	readonly pendingNettoDrafts = computed<SalaryDraftNettoRow[]>(() =>
		this.pendingDrafts()
			.filter((draft) => draft.source === 'gratyfikant' || draft.source === 'enova')
			.map((draft) => {
				const payload = draft.payload as SalaryDraftNettoPayload;
				return {
					id: draft.id,
					label: draft.source === 'gratyfikant' ? 'Gratyfikant' : 'Enova',
					currentNetto: payload.current_netto,
					proposedNetto: payload.proposed_netto,
					diff: payload.current_netto === null ? null : payload.proposed_netto - payload.current_netto,
				};
			}),
	);

	readonly pendingEventDrafts = computed<SalaryDraftEventRow[]>(() =>
		this.pendingDrafts()
			.filter((draft) => draft.source === 'nawigator_event')
			.map((draft) => {
				const payload = draft.payload as SalaryDraftEventPayload;
				return {
					id: draft.id,
					symbol: payload.symbol,
					dateFrom: String(payload.date_from).slice(0, 16),
					dateTo: String(payload.date_to).slice(0, 16),
					person: payload.person,
					changeType: payload.change_type ?? 'new',
					currentDateFrom: payload.current_date_from ? String(payload.current_date_from).slice(0, 16) : null,
					currentDateTo: payload.current_date_to ? String(payload.current_date_to).slice(0, 16) : null,
					currentSymbol: payload.current_symbol ?? null,
				};
			}),
	);

	readonly pendingBonusDrafts = computed<SalaryDraftBonusRow[]>(() =>
		this.pendingDrafts()
			.filter((draft) => draft.source === 'navbridge_bonus')
			.map((draft) => {
				const payload = draft.payload as SalaryDraftBonusPayload;
				return {
					id: draft.id,
					name: payload.name,
					valueGrosze: Math.round(Number(payload.value) * 100),
					date: payload.date,
					currentValueGrosze:
						payload.current_value === undefined ? null : Math.round(Number(payload.current_value) * 100),
					currentDate: payload.current_date ?? null,
				};
			}),
	);

	readonly selectedMonthLabel = computed(() => {
		const month = this.month();
		if (!month) return '';
		const date = new Date(`${month}-01T00:00:00`);
		return date.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
	});

	readonly activeWorkRate = computed<number | null>(() => {
		const month = this.month();
		const rates = this.workRates();
		if (!month || !rates.length) return null;

		const { monthStart, monthEnd } = getMonthRange(month);

		const matching = rates.filter((rate) => {
			const dateFrom = new Date(rate.dateFrom);
			const dateTo = rate.dateTo ? new Date(rate.dateTo) : null;
			if (dateFrom > monthEnd) return false;
			if (dateTo && dateTo < monthStart) return false;
			return true;
		});

		if (!matching.length) return null;

		const latest = matching.reduce((acc, cur) => (new Date(cur.dateFrom) > new Date(acc.dateFrom) ? cur : acc));
		const value = Number(latest.workRate);
		return Number.isFinite(value) ? Math.round(value * 100) : null;
	});

	readonly pracaEvents = computed<PracaEventRow[]>(() => {
		const events = this.tourismEvents();
		const month = this.month();
		if (!month) return [];

		const { monthStart, monthEnd } = getMonthRange(month);
		const rate = this.activeWorkRate() ?? PRACA_FALLBACK_RATE;

		return events
			.filter((event) => event.name === 'PRACA' || event.name === 'PRACAX2')
			.map((event) => {
				const from = new Date(event.dateFrom);
				const to = new Date(event.dateTo);
				const days = countDaysInMonth(from, to, monthStart, monthEnd);
				return {
					id: event.id,
					dateFrom: event.dateFrom,
					dateTo: event.dateTo,
					daysInMonth: days,
					amount: days * rate,
					excluded: event.excludedFromSettlement,
					comment: event.settlementExclusionComment ?? null,
					monthSpan: eventMonthSpan(from, to, month),
					nawigatorEventId: event.nawigatorEventId ?? null,
				};
			});
	});

	readonly pracaTotal = computed<number | null>(() => {
		const rows = this.pracaEvents();
		if (!rows.length) return null;
		return rows.filter((row) => !row.excluded).reduce((sum, row) => sum + row.amount, 0);
	});

	readonly pracaUsesFallbackRate = computed(() => this.activeWorkRate() === null && this.pracaEvents().length > 0);

	/**
	 * "Premia sezonowa": what the announced seasons pay for this driver's worked days. Must stay in
	 * step with DriverSalaryV2Formula - it walks the same non-excluded PRACA/PRACAX2 days (so a monthly-base
	 * driver earns it too) and lets a driver-kind season replace the company-wide one for a day
	 * rather than stacking on top of it. The seasons arrive already clipped to the month and already
	 * filtered to this driver's kind.
	 */
	readonly seasonalBonusEarned = computed<{ days: number; pln: number }>(() => {
		const seasons = this.seasonalBonuses();
		const month = this.month();
		if (!seasons.length || !month) return { days: 0, pln: 0 };

		const { monthStart, monthEnd } = getMonthRange(month);
		let days = 0;
		let pln = 0;

		for (const row of this.pracaEvents()) {
			if (row.excluded) continue;

			const from = new Date(row.dateFrom);
			const to = new Date(row.dateTo);
			const start = from > monthStart ? new Date(from) : new Date(monthStart);
			const end = to < monthEnd ? new Date(to) : new Date(monthEnd);
			start.setHours(12, 0, 0, 0);
			end.setHours(12, 0, 0, 0);

			for (const day = start; day <= end; day.setDate(day.getDate() + 1)) {
				const rate = seasonalRateForDay(seasons, day);
				if (rate !== 0) {
					days += 1;
					pln += rate;
				}
			}
		}

		return { days, pln };
	});

	readonly seasonalBonusDays = computed<number>(() => this.seasonalBonusEarned().days);

	readonly seasonalBonusPln = computed<number>(() => this.seasonalBonusEarned().pln);

	readonly activePeriod = computed<DriverWorkRate | null>(() => {
		const month = this.month();
		const rates = this.workRates();
		if (!month || !rates.length) return null;

		const firstDay = getMonthRange(month).dateFromStr;

		const matching = rates.filter((rate) => {
			const from = String(rate.dateFrom).slice(0, 10);
			const to = rate.dateTo ? String(rate.dateTo).slice(0, 10) : null;
			if (from > firstDay) return false;
			if (to && to < firstDay) return false;
			return true;
		});

		if (!matching.length) return null;

		return matching.reduce((acc, cur) =>
			String(cur.dateFrom).slice(0, 10) > String(acc.dateFrom).slice(0, 10) ? cur : acc,
		);
	});

	readonly isMonthlyBase = computed(() => Number(this.activePeriod()?.periodType) === PERIOD_TYPE_MONTHLY);

	readonly monthlyBaseAmount = computed<number | null>(() => {
		const period = this.activePeriod();
		if (Number(period?.periodType) !== PERIOD_TYPE_MONTHLY) return null;
		const amount = Number(period?.monthlyAmount);
		return Number.isFinite(amount) && amount > 0 ? amount : 0;
	});

	readonly pracaBaseGrosze = computed<number>(() =>
		this.isMonthlyBase() ? (this.monthlyBaseAmount() ?? 0) : (this.pracaTotal() ?? 0),
	);

	readonly technicznyEvents = computed<TechnicznyEventRow[]>(() => {
		const events = this.tourismEvents();
		const month = this.month();
		if (!month) return [];

		const { monthStart, monthEnd } = getMonthRange(month);

		return events
			.filter((event) => event.name === 'TECHNICZNY')
			.filter(
				(event) => countDaysInMonth(new Date(event.dateFrom), new Date(event.dateTo), monthStart, monthEnd) > 0,
			)
			.map((event) => ({
				id: event.id,
				dateFrom: event.dateFrom,
				dateTo: event.dateTo,
				amount: TECHNICZNY_FLAT_AMOUNT,
				excluded: event.excludedFromSettlement,
				comment: event.settlementExclusionComment ?? null,
				monthSpan: eventMonthSpan(new Date(event.dateFrom), new Date(event.dateTo), month),
				nawigatorEventId: event.nawigatorEventId ?? null,
			}));
	});

	readonly technicznyTotal = computed<number | null>(() => {
		const rows = this.technicznyEvents();
		if (!rows.length) return null;
		return rows.filter((row) => !row.excluded).reduce((sum, row) => sum + row.amount, 0);
	});

	readonly dojazdEvents = computed<DojazdEventRow[]>(() => {
		const events = this.tourismEvents();
		const month = this.month();
		if (!month) return [];

		const { monthStart, monthEnd } = getMonthRange(month);

		return events
			.filter((event) => event.name === 'DOJAZD')
			.filter(
				(event) => countDaysInMonth(new Date(event.dateFrom), new Date(event.dateTo), monthStart, monthEnd) > 0,
			)
			.map((event) => ({
				id: event.id,
				dateFrom: event.dateFrom,
				dateTo: event.dateTo,
				amount: DOJAZD_FLAT_AMOUNT,
				excluded: event.excludedFromSettlement,
				comment: event.settlementExclusionComment ?? null,
				monthSpan: eventMonthSpan(new Date(event.dateFrom), new Date(event.dateTo), month),
				nawigatorEventId: event.nawigatorEventId ?? null,
			}));
	});

	readonly dojazdTotal = computed<number | null>(() => {
		const rows = this.dojazdEvents();
		if (!rows.length) return null;
		return rows.filter((row) => !row.excluded).reduce((sum, row) => sum + row.amount, 0);
	});

	/**
	 * `eur` is what the driver owes in the currency they took it in - shown, never deducted.
	 * `eurPln` is that same balance in PLN and is the figure the payout actually settles: the backend
	 * values the whole balance at one NBP rate, so euro taken and handed back cancels to nothing.
	 */
	readonly ledgerPendingTotals = computed<{ pln: number; eur: number; eurPln: number }>(() => {
		const summary = this.ledgerPendingSummary();
		return { pln: summary.totalPln, eur: summary.totalEur, eurPln: summary.totalEurPln };
	});

	readonly settlementRows = computed<SettlementBonusRow[]>(() =>
		this.settlementBonuses().map((b) => ({
			...b,
			amountGrosze: b.valueGrosze ?? bonusValueToGrosze(b.value),
			plnGrosze: b.valuePlnGrosze ?? b.valueGrosze ?? bonusValueToGrosze(b.value),
		})),
	);

	readonly settlementPositive = computed<SettlementBonusRow[]>(() =>
		this.settlementRows().filter((b) => b.amountGrosze >= 0),
	);

	readonly settlementNegative = computed<SettlementBonusRow[]>(() =>
		this.settlementRows().filter((b) => b.amountGrosze < 0),
	);

	readonly settlementCounted = computed<SettlementBonusRow[]>(() =>
		this.settlementRows().filter((b) => b.status === 'CONFIRMED' || b.status === 'SETTLED'),
	);

	readonly settlementPositiveTotal = computed(() =>
		this.settlementPositive()
			.filter((b) => b.status === 'CONFIRMED' || b.status === 'SETTLED')
			.reduce((sum, b) => sum + b.plnGrosze, 0),
	);

	readonly settlementNegativeTotal = computed(() =>
		this.settlementNegative()
			.filter((b) => b.status === 'CONFIRMED' || b.status === 'SETTLED')
			.reduce((sum, b) => sum + b.plnGrosze, 0),
	);

	readonly settlementPositivePending = computed(() => this.settlementPositive().filter((b) => b.status === 'NEW'));

	readonly settlementNegativePending = computed(() => this.settlementNegative().filter((b) => b.status === 'NEW'));

	readonly settlementPositivePendingTotal = computed(() =>
		this.settlementPositivePending().reduce((sum, b) => sum + b.plnGrosze, 0),
	);

	readonly settlementNegativePendingTotal = computed(() =>
		this.settlementNegativePending().reduce((sum, b) => sum + b.plnGrosze, 0),
	);

	readonly bonusesMissingPln = computed(() => this.settlementCounted().filter((b) => b.plnConversionMissing).length);

	readonly nawigatorTotalPln = computed(
		() => this.pracaBaseGrosze() + (this.technicznyTotal() ?? 0) + (this.dojazdTotal() ?? 0),
	);

	readonly settlementNetPln = computed(() => this.settlementCounted().reduce((sum, b) => sum + b.plnGrosze, 0));

	readonly settlementNetEur = computed(() => 0);

	/**
	 * How this month splits the kasa: what it deducts from this payout and what it leaves for the
	 * next one. Mirrors the backend KasaSettlementResolver - a settled month reports the split frozen
	 * at settle time, because the settlement emptied the balance this would otherwise read.
	 */
	readonly kasaSplit = computed<KasaSplit>(() => {
		const balance = this.ledgerPendingTotals();

		if (this.isSettled() && this.hasFrozenKasaSplit()) {
			return {
				settledPln: this.kasaSettlePlnChoice() ?? 0,
				settledEur: this.kasaSettleEurChoice() ?? 0,
				settledEurPln: this.kasaSettleEurPlnFrozen() ?? 0,
				carriedPln: this.kasaCarriedPlnFrozen() ?? 0,
				carriedEur: this.kasaCarriedEurFrozen() ?? 0,
			};
		}

		const settledPln = clampToBalance(this.kasaSettlePlnChoice(), balance.pln);
		const settledEur = clampToBalance(this.kasaSettleEurChoice(), balance.eur);

		return {
			settledPln,
			settledEur,
			settledEurPln: shareOfEurInPln(settledEur, balance.eur, balance.eurPln),
			carriedPln: balance.pln - settledPln,
			carriedEur: balance.eur - settledEur,
		};
	});

	readonly kasaCarriesAnything = computed(() => {
		const split = this.kasaSplit();
		return split.carriedPln !== 0 || split.carriedEur !== 0;
	});

	readonly kasaSettledEurPlnDeducted = computed(() => (this.kasaEurInCash() ? 0 : this.kasaSplit().settledEurPln));

	readonly kasaPayoutEur = computed(() => (this.kasaEurInCash() ? -this.kasaSplit().settledEur : 0));

	readonly hasEurKasa = computed(
		() => this.ledgerPendingTotals().eur !== 0 || this.kasaSplit().settledEur !== 0 || this.kasaEurInCash(),
	);

	/** Everything the kasa takes off this payout, in the single currency it is taken off in. */
	readonly kasaDeductedPln = computed(() => this.kasaSplit().settledPln + this.kasaSettledEurPlnDeducted());

	readonly effectiveDietaPln = computed(() => (this.enovaTotal() ?? 0) - (this.gratyfikantTotal() ?? 0));

	readonly sumAllPln = computed<number | null>(() => {
		const nawigator = this.nawigatorTotalPln();
		const dieta = this.effectiveDietaPln();
		const gratyfikant = this.gratyfikantTotal() ?? 0;
		const settlements = this.settlementNetPln();
		const kasa = this.kasaSplit().settledPln;
		const kasaEurPln = this.kasaSettledEurPlnDeducted();
		const seasonal = this.seasonalBonusPln();
		if (
			nawigator === 0 &&
			dieta === 0 &&
			gratyfikant === 0 &&
			settlements === 0 &&
			kasa === 0 &&
			kasaEurPln === 0 &&
			seasonal === 0 &&
			this.kasaPayoutEur() === 0
		)
			return null;
		return nawigator + seasonal - dieta - gratyfikant + settlements - kasa - kasaEurPln;
	});

	/** The kasa exactly as the payout deducts it, per currency. */
	readonly kasaPendingPln = computed(() => this.kasaSplit().settledPln);

	readonly kasaPendingEur = computed(() => this.kasaSplit().settledEur);

	/**
	 * Nothing is carved out of the payout any more: the kasa is deducted inside sumAllPln and what is
	 * left is handed over whole. The old "Gotówka / Kasa" split fields were the only thing that could
	 * disagree with the sum, so there is no split to verify either.
	 */
	readonly cashToPayoutPln = computed(() => this.sumAllPln() ?? 0);

	readonly cashToPayoutEur = computed(() => this.kasaPayoutEur());

	/** What is owed in cash, minus what was actually counted out. */
	readonly cashPayoutRemainingPln = computed(() => this.cashToPayoutPln() - (this.cashPayoutPln() ?? 0));

	readonly cashPayoutRemainingEur = computed(() => this.cashToPayoutEur() - (this.cashPayoutEur() ?? 0));

	readonly cashPayoutComplete = computed(
		() => Math.abs(this.cashPayoutRemainingPln()) <= 1 && Math.abs(this.cashPayoutRemainingEur()) <= 1,
	);

	/** Nothing to report on a month that neither owes cash nor had any handed over. */
	readonly hasCashPayout = computed(
		() =>
			this.cashToPayoutPln() !== 0 ||
			this.cashToPayoutEur() !== 0 ||
			this.cashPayoutPln() !== null ||
			this.cashPayoutEur() !== null,
	);

	readonly hasActiveGratyfikantMapping = computed(() => {
		const month = this.month();
		return !!month && hasPeriodInMonth(this.gratyfikantMappings(), month);
	});

	readonly hasActiveEnovaMapping = computed(() => {
		const month = this.month();
		return !!month && hasPeriodInMonth(this.enovaMappings(), month);
	});

	readonly gratyfikantDisabled = computed(
		() => this.isSettled() || this.gratyfikantLoading() || !this.hasActiveGratyfikantMapping(),
	);
	readonly gratyfikantTooltip = computed(() => {
		if (this.isSettled()) return this.settledPullTooltip;
		const loading = this.integrationLoading() || this.gratyfikantLoading();
		if (loading) return '';
		if (!this.hasActiveGratyfikantMapping()) {
			return 'Brak konfiguracji Gratyfikant dla tego kierowcy w wybranym miesiącu. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly nawigatorDisabled = computed(
		() =>
			this.isSettled() ||
			this.nawigatorLoading() ||
			this.integrationLoading() ||
			!this.integration()?.nawigator_id,
	);
	readonly nawigatorTooltip = computed(() => {
		if (this.isSettled()) return this.settledPullTooltip;
		if (this.integrationLoading()) return '';
		if (!this.integration()?.nawigator_id) {
			return 'Brak ID Nawigator dla tego kierowcy. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly enovaDisabled = computed(
		() => this.isSettled() || this.integrationLoading() || !this.hasActiveEnovaMapping(),
	);
	readonly enovaTooltip = computed(() => {
		if (this.isSettled()) return this.settledPullTooltip;
		if (this.integrationLoading()) return '';
		if (!this.hasActiveEnovaMapping()) {
			return 'Brak Enova ID dla tego kierowcy w wybranym miesiącu. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly navbridgeBonusesDisabled = computed(
		() =>
			this.isSettled() ||
			this.navbridgeBonusesLoading() ||
			this.integrationLoading() ||
			!this.integration()?.nawigator_id,
	);
	readonly navbridgeBonusesTooltip = computed(() => {
		if (this.isSettled()) return this.settledPullTooltip;
		if (this.integrationLoading()) return '';
		if (!this.integration()?.nawigator_id) {
			return 'Brak ID Nawigator dla tego kierowcy. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly selectedDriverId = computed(() => this.driverId());
	readonly selectedMonth = computed(() => this.month());

	private readonly destroyRef = inject(DestroyRef);
	private readonly api = inject(Data2Service);
	private readonly ledgerApi = inject(DriverLedgerApiService);
	private readonly apiService = inject(DriversSalaryV2ApiService);
	private readonly seasonalBonusApi = inject(SeasonalBonusApiService);
	private readonly bonusActionsApi = inject(SettlementBonusListV2ApiService);

	private readonly driverId = signal<string>('');
	private readonly month = signal<string>('');

	private readonly settledPullTooltip = 'Miesiąc jest rozliczony. Cofnij rozliczenie, aby pobrać dane.';

	setContext(driverId: string, month: string): void {
		if (this.driverId() === driverId && this.month() === month) return;
		this.driverId.set(driverId);
		this.month.set(month);
		this.resetData();
	}

	reload(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month) return;

		this.loadSalaryFromCache();
		this.loadIntegrations();
		this.loadTourismEvents();
		this.loadWorkRates();
		this.loadSettlementBonuses();
		this.loadSalaryDrafts();
		this.loadSeasonalBonus();
	}

	rejectDraft(draftId: number): void {
		if (this.draftActionBusy()) return;

		this.draftRejectingId.set(draftId);
		this.draftsError.set(null);

		this.apiService
			.rejectSalaryDraft(this.driverId(), draftId)
			.pipe(
				catchError((err) => {
					this.draftsError.set(extractErrorMessage(err, 'Nie udało się odrzucić zmiany.'));
					return EMPTY;
				}),
				finalize(() => this.draftRejectingId.set(null)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => this.loadSalaryDrafts());
	}

	acceptDraft(draftId: number): void {
		if (this.draftActionBusy() || this.isSettled()) return;

		this.draftAcceptingId.set(draftId);
		this.draftsError.set(null);

		this.apiService
			.acceptSalaryDraft(this.driverId(), draftId)
			.pipe(
				catchError((err) => {
					this.draftsError.set(extractErrorMessage(err, 'Nie udało się zastosować zmiany.'));
					return EMPTY;
				}),
				finalize(() => this.draftAcceptingId.set(null)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.loadSalaryDrafts();
				this.loadSalaryFromCache();
			});
	}

	loadGratyfikant(): void {
		if (this.isSettled()) return;
		this.gratyfikantLoading.set(true);
		this.gratyfikantError.set(null);
		this.gratyfikantSkipped.set([]);
		this.apiService
			.getGratyfikantSalaries(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.gratyfikantError.set(extractErrorMessage(err, 'Nie udało się pobrać danych z Gratyfikant.'));
					return EMPTY;
				}),
				finalize(() => this.gratyfikantLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => {
				const totalZl = response.data.reduce((sum, row) => sum + (Number(row['Netto']) || 0), 0);
				this.gratyfikantTotal.set(Math.round(totalZl * 100));
				this.gratyfikantSkipped.set(response.meta?.skipped ?? []);
				this.gratyfikantLoaded.set(true);
				this.gratyfikantLoadedAt.set(new Date());
				this.loadSalaryDrafts();
			});
	}

	loadNawigator(): void {
		if (this.isSettled()) return;
		this.nawigatorLoading.set(true);
		this.nawigatorError.set(null);
		this.apiService
			.pullNawigatorEvents(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.nawigatorError.set(extractErrorMessage(err, 'Nie udało się pobrać danych z Nawigator.'));
					return EMPTY;
				}),
				finalize(() => this.nawigatorLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((stats) => {
				this.nawigatorPullStats.set(stats);
				this.nawigatorLoadedAt.set(new Date());
				this.loadTourismEvents();
				this.loadSalaryDrafts();
			});
	}

	loadEnova(): void {
		if (this.isSettled()) return;
		this.enovaLoading.set(true);
		this.enovaError.set(null);
		this.apiService
			.getEnovaSalaries(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.enovaError.set(extractErrorMessage(err, 'Nie udało się pobrać danych z Enova.'));
					return EMPTY;
				}),
				finalize(() => this.enovaLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => {
				const totalZl = data.reduce((sum, row) => sum + (Number(row['amount']) || 0), 0);
				this.enovaTotal.set(Math.round(totalZl * 100));
				this.enovaLoaded.set(true);
				this.enovaLoadedAt.set(new Date());
				this.loadSalaryDrafts();
			});
	}

	loadNavbridgeBonuses(): void {
		if (this.isSettled()) return;
		this.navbridgeBonusesLoading.set(true);
		this.navbridgeBonusesError.set(null);
		this.apiService
			.pullNavbridgeBonuses(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.navbridgeBonusesError.set(
						extractErrorMessage(err, 'Nie udało się pobrać dodatków z NavBridge.'),
					);
					return EMPTY;
				}),
				finalize(() => this.navbridgeBonusesLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((stats) => {
				this.navbridgeBonusesStats.set(stats);
				this.navbridgeBonusesLoadedAt.set(new Date());
				this.refreshSettlementBonuses();
				this.loadSalaryDrafts();
			});
	}

	refreshSettlementBonuses(): void {
		this.loadSettlementBonuses();
	}

	recalculateBonusPln(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month || this.recalculatePlnLoading()) return;

		this.recalculatePlnLoading.set(true);
		this.settlementActionError.set(null);

		this.apiService
			.recalculateSettlementBonusPln(driverId, month)
			.pipe(
				catchError((err) => {
					this.settlementActionError.set(extractErrorMessage(err, 'Nie udało się przeliczyć kwot.'));
					return EMPTY;
				}),
				finalize(() => this.recalculatePlnLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => this.refreshSettlementBonuses());
	}

	confirmBonus(bonusId: string): void {
		this.runBonusAction(() => this.bonusActionsApi.confirmBonuses([Number(bonusId)], true));
	}

	unlockBonus(bonusId: string): void {
		this.runBonusAction(() => this.bonusActionsApi.confirmBonuses([Number(bonusId)], false));
	}

	rejectBonus(bonusId: string, reason: string | null): void {
		this.runBonusAction(() => this.bonusActionsApi.rejectBonuses([Number(bonusId)], true, reason));
	}

	restoreBonus(bonusId: string): void {
		this.runBonusAction(() => this.bonusActionsApi.rejectBonuses([Number(bonusId)], false, null));
	}

	setEventExclusion(eventId: string, excluded: boolean, comment: string | null): void {
		if (this.isSettled()) return;

		const month = this.month();
		if (!month) return;

		const snapshot = this.tourismEvents();
		const normalizedComment = comment && comment.trim() !== '' ? comment : null;

		this.tourismEvents.set(
			snapshot.map((event) =>
				event.id === eventId
					? { ...event, excludedFromSettlement: excluded, settlementExclusionComment: normalizedComment }
					: event,
			),
		);
		this.eventExclusionError.set(null);

		this.apiService
			.setEventExclusion(this.driverId(), eventId, { month, excluded, comment: normalizedComment })
			.pipe(
				catchError((err) => {
					this.tourismEvents.set(snapshot);
					this.eventExclusionError.set(extractErrorMessage(err, 'Nie udało się zapisać wykluczenia wpisu.'));
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => {
				this.tourismEvents.set(
					this.tourismEvents().map((event) =>
						event.id === eventId
							? {
									...event,
									excludedFromSettlement: response.excludedFromSettlement,
									settlementExclusionComment: response.settlementExclusionComment,
								}
							: event,
					),
				);
			});
	}

	saveNotes(): void {
		if (this.isSettled() || this.notesSaving()) return;

		this.notesSaving.set(true);
		this.notesError.set(null);

		this.apiService
			.saveNotes(this.driverId(), this.month(), {
				settlement_notes: this.settlementNotes(),
				payment_notes: this.paymentNotes(),
			})
			.pipe(
				catchError((err) => {
					this.notesError.set(extractErrorMessage(err, 'Nie udało się zapisać uwag.'));
					return EMPTY;
				}),
				finalize(() => this.notesSaving.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => this.applySalaryCacheResponse(data));
	}

	saveCashPayoutDate(value: string | null): void {
		if (value === this.cashPayoutDate()) return;

		const previous = this.cashPayoutDate();
		this.cashPayoutDate.set(value);
		this.persistCashPayout({ cash_payout_date: value }, 'Nie udało się zapisać daty wydania gotówki.', () =>
			this.cashPayoutDate.set(previous),
		);
	}

	saveCashPayoutPln(value: number | null): void {
		if (value === this.cashPayoutPln()) return;

		const previous = this.cashPayoutPln();
		this.cashPayoutPln.set(value);
		this.persistCashPayout({ cash_payout_pln: value }, 'Nie udało się zapisać wydanej gotówki PLN.', () =>
			this.cashPayoutPln.set(previous),
		);
	}

	saveCashPayoutEur(value: number | null): void {
		if (value === this.cashPayoutEur()) return;

		const previous = this.cashPayoutEur();
		this.cashPayoutEur.set(value);
		this.persistCashPayout({ cash_payout_eur: value }, 'Nie udało się zapisać wydanej gotówki EUR.', () =>
			this.cashPayoutEur.set(previous),
		);
	}

	saveKasaSettlePln(value: number | null): void {
		if (value === this.kasaSettlePlnChoice()) return;

		const previous = this.kasaSettlePlnChoice();
		this.kasaSettlePlnChoice.set(value);
		this.persistKasaSettlement({ kasa_settle_pln: value }, 'Nie udało się zapisać kwoty kasy PLN.', () =>
			this.kasaSettlePlnChoice.set(previous),
		);
	}

	saveKasaEurInCash(value: boolean): void {
		if (value === this.kasaEurInCash()) return;

		const previous = this.kasaEurInCash();
		this.kasaEurInCash.set(value);
		this.persistKasaSettlement({ kasa_eur_in_cash: value }, 'Nie udało się zapisać sposobu rozliczenia EUR.', () =>
			this.kasaEurInCash.set(previous),
		);
	}

	saveKasaSettleEur(value: number | null): void {
		if (value === this.kasaSettleEurChoice()) return;

		const previous = this.kasaSettleEurChoice();
		this.kasaSettleEurChoice.set(value);
		this.persistKasaSettlement({ kasa_settle_eur: value }, 'Nie udało się zapisać kwoty kasy EUR.', () =>
			this.kasaSettleEurChoice.set(previous),
		);
	}

	/** Back to the default: both currencies deducted in full, nothing carried. */
	settleWholeKasa(): void {
		if (this.kasaSettlePlnChoice() === null && this.kasaSettleEurChoice() === null) return;

		const previousPln = this.kasaSettlePlnChoice();
		const previousEur = this.kasaSettleEurChoice();
		this.kasaSettlePlnChoice.set(null);
		this.kasaSettleEurChoice.set(null);
		this.persistKasaSettlement(
			{ kasa_settle_pln: null, kasa_settle_eur: null },
			'Nie udało się zapisać kwoty kasy.',
			() => {
				this.kasaSettlePlnChoice.set(previousPln);
				this.kasaSettleEurChoice.set(previousEur);
			},
		);
	}

	settleMonth(): void {
		if (this.isSettled() || this.settleLoading()) return;

		this.settleLoading.set(true);
		this.settleError.set(null);

		this.apiService
			.settleMonth(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.settleError.set(extractErrorMessage(err, 'Nie udało się rozliczyć miesiąca.'));
					return EMPTY;
				}),
				finalize(() => this.settleLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => {
				this.applySalaryCacheResponse(data);
				this.loadPendingLedger();
				this.refreshSettlementBonuses();
				this.loadSalaryDrafts();
			});
	}

	unsettleMonth(): void {
		if (!this.isSettled() || this.settleLoading()) return;

		this.settleLoading.set(true);
		this.settleError.set(null);

		this.apiService
			.unsettleMonth(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.settleError.set(extractErrorMessage(err, 'Nie udało się cofnąć rozliczenia.'));
					return EMPTY;
				}),
				finalize(() => this.settleLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => {
				// An empty body means the endpoint found nothing to revert. Clearing the state here
				// would show an open month while the ledger stayed settled, so the server's answer is
				// re-read instead of guessed at.
				if (!data) {
					this.settleError.set('Nie cofnięto rozliczenia — ten miesiąc nie był rozliczony.');
					this.loadSalaryFromCache();
					return;
				}

				this.applySalaryCacheResponse(data);
				this.loadPendingLedger();
				this.refreshSettlementBonuses();
				this.loadSalaryDrafts();
			});
	}

	private loadSalaryDrafts(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month) return;

		this.draftsLoading.set(true);
		this.apiService
			.getSalaryDrafts(driverId, month)
			.pipe(
				catchError(() => {
					this.salaryDrafts.set([]);
					return EMPTY;
				}),
				finalize(() => this.draftsLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((drafts) => this.salaryDrafts.set(drafts));
	}

	private resetData(): void {
		this.gratyfikantTotal.set(null);
		this.gratyfikantLoading.set(false);
		this.gratyfikantLoaded.set(false);
		this.gratyfikantLoadedAt.set(null);
		this.gratyfikantError.set(null);
		this.gratyfikantSkipped.set([]);
		this.enovaTotal.set(null);
		this.enovaLoading.set(false);
		this.enovaLoaded.set(false);
		this.enovaLoadedAt.set(null);
		this.enovaError.set(null);
		this.nawigatorLoading.set(false);
		this.nawigatorLoadedAt.set(null);
		this.nawigatorError.set(null);
		this.nawigatorPullStats.set(null);
		this.navbridgeBonusesLoading.set(false);
		this.navbridgeBonusesLoadedAt.set(null);
		this.navbridgeBonusesError.set(null);
		this.navbridgeBonusesStats.set(null);
		this.tourismEvents.set([]);
		this.tourismEventsLoading.set(false);
		this.eventExclusionError.set(null);
		this.integration.set(null);
		this.gratyfikantMappings.set([]);
		this.enovaMappings.set([]);
		this.integrationLoading.set(false);
		this.workRates.set([]);
		this.seasonalBonuses.set([]);
		this.ledgerPendingSummary.set(EMPTY_LEDGER_PENDING_SUMMARY);
		this.settlementBonuses.set([]);
		this.settlementBonusesLoading.set(false);
		this.settlementActionLoading.set(false);
		this.settlementActionError.set(null);
		this.settlementNotes.set('');
		this.paymentNotes.set('');
		this.settledAt.set(null);
		this.settledBy.set(null);
		this.salaryId.set(null);
		this.notesSaving.set(false);
		this.notesError.set(null);
		this.settleLoading.set(false);
		this.settleError.set(null);
		this.cashPayoutPln.set(null);
		this.cashPayoutEur.set(null);
		this.cashPayoutDate.set(null);
		this.kasaSettlePlnChoice.set(null);
		this.kasaSettleEurChoice.set(null);
		this.kasaSettleEurPlnFrozen.set(null);
		this.kasaCarriedPlnFrozen.set(null);
		this.kasaCarriedEurFrozen.set(null);
		this.kasaEurInCash.set(false);
		this.kasaSettlementSaving.set(false);
		this.kasaSettlementError.set(null);
		this.cashPayoutBy.set(null);
		this.cashPayoutSaving.set(false);
		this.cashPayoutError.set(null);
		this.salaryDrafts.set([]);
		this.draftsLoading.set(false);
		this.draftsError.set(null);
		this.draftRejectingId.set(null);
	}

	/**
	 * Each field is sent on its own and the local value is kept as the truth - the response is only
	 * mined for the id, so a save that lands after a newer edit cannot resurrect the old amount.
	 */
	private persistCashPayout(payload: SalaryCashPayoutPayload, errorMessage: string, rollback: () => void): void {
		this.cashPayoutSaving.set(true);
		this.cashPayoutError.set(null);

		this.apiService
			.saveCashPayout(this.driverId(), this.month(), payload)
			.pipe(
				catchError((err) => {
					rollback();
					this.cashPayoutError.set(extractErrorMessage(err, errorMessage));
					return EMPTY;
				}),
				finalize(() => this.cashPayoutSaving.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => this.salaryId.set(data?.id ?? this.salaryId()));
	}

	private persistKasaSettlement(
		payload: SalaryKasaSettlementPayload,
		errorMessage: string,
		rollback: () => void,
	): void {
		this.kasaSettlementSaving.set(true);
		this.kasaSettlementError.set(null);

		this.apiService
			.saveKasaSettlement(this.driverId(), this.month(), payload)
			.pipe(
				catchError((err) => {
					rollback();
					this.kasaSettlementError.set(extractErrorMessage(err, errorMessage));
					return EMPTY;
				}),
				finalize(() => this.kasaSettlementSaving.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => this.salaryId.set(data?.id ?? this.salaryId()));
	}

	private runBonusAction(actionFactory: () => Observable<BulkActionResult>): void {
		if (this.isSettled() || this.settlementActionLoading()) return;

		this.settlementActionLoading.set(true);
		this.settlementActionError.set(null);

		actionFactory()
			.pipe(
				catchError((err) => {
					this.settlementActionError.set(extractErrorMessage(err, 'Nie udało się wykonać akcji.'));
					return EMPTY;
				}),
				finalize(() => this.settlementActionLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((result) => {
				const failure = result.data.failed[0];
				if (failure) {
					this.settlementActionError.set(failure.message);
				}
				this.refreshSettlementBonuses();
			});
	}

	private loadPendingLedger(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month) return;

		const settledSalaryId = this.isSettled() ? this.salaryId() : null;

		this.ledgerApi
			.getPendingSummary(driverId, settledSalaryId ?? undefined)
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((summary) => this.ledgerPendingSummary.set(summary));
	}

	private loadSettlementBonuses(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month) return;

		this.settlementBonusesLoading.set(true);
		this.apiService
			.getSettlementBonusList(driverId, month)
			.pipe(
				catchError(() => {
					this.settlementBonuses.set([]);
					return EMPTY;
				}),
				finalize(() => this.settlementBonusesLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((list) => this.settlementBonuses.set(list));
	}

	private loadWorkRates(): void {
		this.api.get(
			`/driver/driverRate/?driver=${this.driverId()}`,
			(result: false | { data: DriverWorkRate[] }, error: unknown) => {
				if (error || !result) return;
				this.workRates.set(result.data ?? []);
			},
		);
	}

	private loadSalaryFromCache(): void {
		this.apiService
			.getSalaryCache(this.driverId(), this.month())
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.loadPendingLedger()),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => this.applySalaryCacheResponse(data));
	}

	private applySalaryCacheResponse(data: SalaryCacheResponse | null): void {
		if (data?.gratyfikant_netto) {
			this.gratyfikantTotal.set(data.gratyfikant_netto);
			this.gratyfikantLoaded.set(true);
		}
		if (data?.enova_netto) {
			this.enovaTotal.set(data.enova_netto);
			this.enovaLoaded.set(true);
		}
		this.settlementNotes.set(data?.settlement_notes ?? '');
		this.paymentNotes.set(data?.payment_notes ?? '');
		this.settledAt.set(data?.settled_at ?? null);
		this.settledBy.set(data?.settled_by ?? null);
		this.salaryId.set(data?.id ?? null);
		this.cashPayoutPln.set(data?.cash_payout_pln ?? null);
		this.cashPayoutEur.set(data?.cash_payout_eur ?? null);
		this.cashPayoutDate.set(data?.cash_payout_date ?? null);
		this.cashPayoutBy.set(data?.cash_payout_by ?? null);
		this.kasaSettlePlnChoice.set(data?.kasa_settle_pln ?? null);
		this.kasaSettleEurChoice.set(data?.kasa_settle_eur ?? null);
		this.kasaSettleEurPlnFrozen.set(data?.kasa_settle_eur_pln ?? null);
		this.kasaCarriedPlnFrozen.set(data?.kasa_carried_pln ?? null);
		this.kasaCarriedEurFrozen.set(data?.kasa_carried_eur ?? null);
		this.kasaEurInCash.set(data?.kasa_eur_in_cash ?? false);
	}

	/**
	 * A month settled before partial settlement existed froze nothing, so null keeps its column
	 * meaning - the whole balance - instead of reading as "deducted nothing".
	 */
	private hasFrozenKasaSplit(): boolean {
		return (
			this.kasaSettlePlnChoice() !== null ||
			this.kasaSettleEurChoice() !== null ||
			this.kasaCarriedPlnFrozen() !== null ||
			this.kasaCarriedEurFrozen() !== null
		);
	}

	/**
	 * The seasons belong to a date range and a driver kind, not to this driver's payout: the backend
	 * resolves both and hands back what applies, so this screen's recomputed sum matches the settled
	 * amount rather than drifting from it.
	 */
	private loadSeasonalBonus(): void {
		this.seasonalBonusApi
			.getForDriver(this.driverId(), this.month())
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((bonuses) => this.seasonalBonuses.set(bonuses));
	}

	private loadTourismEvents(): void {
		this.tourismEventsLoading.set(true);
		this.apiService
			.getTourismEvents(this.driverId(), this.month())
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.tourismEventsLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((events) => this.tourismEvents.set(events));
	}

	private loadIntegrations(): void {
		this.integrationLoading.set(true);

		this.apiService
			.getIntegration(this.driverId())
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.integrationLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((integration) => this.integration.set(integration));

		this.apiService
			.getGratyfikantMappings(this.driverId())
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((mappings) => this.gratyfikantMappings.set(mappings));

		this.apiService
			.getEnovaMappings(this.driverId())
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((mappings) => this.enovaMappings.set(mappings));
	}
}
